import { useState, useRef, useEffect } from 'react';
import { Bot, User, Loader2, FileText, FolderOpen, CheckCircle, AlertTriangle, Clock, ArrowRightLeft, Link2, TrendingUp, ClipboardCheck, CalendarClock, MessageCircleQuestion } from 'lucide-react';
import ZeoIcon from './ZeoIcon';

const EVENT_ICONS = {
  ocr_start: <FileText className="w-3.5 h-3.5 text-blue-400" />,
  ocr_ok: <CheckCircle className="w-3.5 h-3.5 text-green-400" />,
  ocr_vision: <Bot className="w-3.5 h-3.5 text-violet-400" />,
  ocr_fehler: <AlertTriangle className="w-3.5 h-3.5 text-red-400" />,
  datei_verschoben: <FolderOpen className="w-3.5 h-3.5 text-amber-400" />,
  datei_fremd_verschoben: <ArrowRightLeft className="w-3.5 h-3.5 text-cyan-400" />,
  aufgabe_erstellt: <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />,
  frist_erstellt: <Clock className="w-3.5 h-3.5 text-orange-400" />,
  vorgang_erstellt: <Link2 className="w-3.5 h-3.5 text-blue-400" />,
  bescheid_verknuepft: <FileText className="w-3.5 h-3.5 text-emerald-400" />,
  betrag_aktualisiert: <TrendingUp className="w-3.5 h-3.5 text-amber-400" />,
  aufgabe_erledigt: <ClipboardCheck className="w-3.5 h-3.5 text-green-400" />,
  weiterbewilligung_aufgabe: <CalendarClock className="w-3.5 h-3.5 text-rose-400" />,
  rueckfrage: <MessageCircleQuestion className="w-3.5 h-3.5 text-yellow-400" />,
};

export function StatusEvent({ event }) {
  const icon = EVENT_ICONS[event.type] || <FileText className="w-3.5 h-3.5 text-slate-400" />;
  let text = '';

  switch (event.type) {
    case 'ocr_start':
      text = `Stufe ${event.stufe || 1}: ${event.datei} wird verarbeitet... (${event.nr}/${event.gesamt})`;
      break;
    case 'ocr_ok':
      text = `${event.datei} erkannt (${event.methode}, ${event.zeichen} Zeichen)`;
      break;
    case 'ocr_vision':
      text = `${event.datei || 'Dokument'} wird per Claude Vision analysiert...`;
      break;
    case 'ocr_fehler':
      text = `${event.datei}: ${event.fehler}`;
      break;
    case 'datei_verschoben':
      text = `${event.datei} → ${event.ordner}`;
      break;
    case 'datei_fremd_verschoben':
      text = `${event.datei} gehört zu ${event.fremder_klient} → verschoben nach ${event.fremder_klient}/aktenuebernahme/`;
      break;
    case 'batch_info':
      text = event.nachricht || `Batch: ${event.batch} von ${event.gesamt}`;
      break;
    case 'sortierung_fertig':
      text = event.nachricht || `${event.verschoben || 0} Dateien einsortiert`;
      if (event.fremd_verschoben > 0) {
        text += ` (${event.fremd_verschoben} an andere Betreute weitergeleitet)`;
      }
      break;
    case 'aufgabe_erstellt':
      text = `Aufgabe: ${event.titel}`;
      break;
    case 'frist_erstellt':
      text = `Frist: ${event.bezeichnung}${event.datum ? ` (${event.datum})` : ''}`;
      break;
    case 'vorgang_erstellt':
      text = `Neuer Vorgang angelegt: ${event.label || event.leistungsart}`;
      break;
    case 'bescheid_verknuepft':
      text = `Bescheid verknüpft: ${event.titel} → ${event.vorgang}`;
      break;
    case 'betrag_aktualisiert':
      text = `Betrag aktualisiert: ${event.alt?.toFixed(2)} → ${event.neu?.toFixed(2)} EUR (${event.leistungsart})`;
      break;
    case 'aufgabe_erledigt':
      text = `Aufgabe erledigt: "${event.titel}" (${event.grund})`;
      break;
    case 'weiterbewilligung_aufgabe':
      text = `Weiterbewilligung: ${event.titel} – Frist: ${event.frist} (Bescheid endet ${event.bescheid_ende})`;
      break;
    case 'rueckfrage':
      text = event.text;
      break;
    case 'ocr_fertig':
      text = `OCR abgeschlossen: ${event.lesbar} lesbar, ${event.nicht_lesbar} nicht lesbar`;
      break;
    default:
      text = event.message || JSON.stringify(event);
  }

  return (
    <div className="flex items-start gap-2 py-1 text-xs text-slate-400 font-mono">
      {icon}
      <span>{text}</span>
    </div>
  );
}

export default function ZeoChat({ events = [], zeoText = '', isStreaming = false, fortschritt = 0, error = null }) {
  const chatEndRef = useRef(null);
  const containerRef = useRef(null);

  const [userScrolledUp, setUserScrolledUp] = useState(false);

  // Nur auto-scrollen wenn User nicht hochgescrollt hat
  useEffect(() => {
    if (!userScrolledUp && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [events.length, zeoText, userScrolledUp]);

  // Erkennen ob User hochscrollt
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 80;
      setUserScrolledUp(!isNearBottom);
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const statusEvents = events.filter(e =>
    ['ocr_start', 'ocr_ok', 'ocr_vision', 'ocr_fehler', 'ocr_fertig',
     'datei_verschoben', 'datei_fremd_verschoben', 'sortierung_fertig',
     'aufgabe_erstellt', 'frist_erstellt', 'batch_info',
     'vorgang_erstellt', 'bescheid_verknuepft', 'betrag_aktualisiert',
     'aufgabe_erledigt', 'weiterbewilligung_aufgabe'].includes(e.type)
  );

  const rueckfragen = events.filter(e => e.type === 'rueckfrage');

  return (
    <div
      ref={containerRef}
      data-testid="zeo-chat"
      className="flex flex-col bg-slate-950 rounded-xl border border-slate-800 overflow-y-auto"
      style={{ maxHeight: '600px' }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-slate-900/80 border-b border-slate-800">
        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0 shadow-md">
          <ZeoIcon size={32} />
        </div>
        <div>
          <div className="text-sm font-semibold text-white">Zeo</div>
          <div className="text-xs text-slate-400">
            {isStreaming ? 'analysiert...' : error ? 'Fehler' : fortschritt >= 100 ? 'Analyse abgeschlossen' : 'KI-Assistent'}
          </div>
        </div>
        {fortschritt > 0 && fortschritt < 100 && (
          <div className="ml-auto flex items-center gap-2">
            <div className="w-32 h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full transition-all duration-500"
                style={{ width: `${fortschritt}%` }}
              />
            </div>
            <span className="text-xs text-slate-400">{fortschritt}%</span>
          </div>
        )}
      </div>

      {/* Chat Body */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {/* Status Events */}
        {statusEvents.length > 0 && (
          <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-800">
            <div className="text-xs font-medium text-slate-500 mb-2">Verarbeitungsprotokoll</div>
            <div className="space-y-0.5 max-h-40 overflow-y-auto">
              {statusEvents.map((evt, i) => (
                <StatusEvent key={i} event={evt} />
              ))}
            </div>
          </div>
        )}

        {/* Zeo Message */}
        {zeoText && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
              <ZeoIcon size={24} />
            </div>
            <div className="flex-1 bg-slate-900/80 rounded-lg px-4 py-3 border border-slate-800">
              <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
                {zeoText.split('===JSON_START===')[0]}
                {isStreaming && (
                  <span className="inline-block w-2 h-4 bg-violet-400 ml-0.5 animate-pulse" />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Rückfragen von Zeo */}
        {rueckfragen.length > 0 && rueckfragen.map((rf, i) => (
          <div key={`rf-${i}`} className="flex items-start gap-3" data-testid="zeo-rueckfrage">
            <MessageCircleQuestion className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="bg-yellow-950/50 rounded-lg px-4 py-3 border border-yellow-800 text-sm text-yellow-200">
              <div className="text-xs font-medium text-yellow-400 mb-1">Rückfrage von Zeo:</div>
              {rf.text}
            </div>
          </div>
        ))}

        {/* Error */}
        {error && (
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="bg-red-950/50 rounded-lg px-4 py-3 border border-red-900 text-sm text-red-300">
              {error}
            </div>
          </div>
        )}

        {/* Loading */}
        {isStreaming && !zeoText && !error && (
          <div className="flex items-center gap-3 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Zeo analysiert die Dokumente...</span>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>
    </div>
  );
}
